import React from 'react';
import { Container, Typography, Box, Paper, Button, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const HrOutsourcing: React.FC = () => {
  // Benefits of HR outsourcing services
  const benefits = [
    {
      title: 'Expert Resources',
      description: 'Access specialized HR professionals without maintaining full-time staff.',
      icon: <VerifiedUserIcon fontSize="large" color="primary" />
    },
    {
      title: 'Cost Efficiency',
      description: 'Reduce overhead costs associated with maintaining an in-house HR department.',
      icon: <AttachMoneyIcon fontSize="large" color="primary" />
    },
    {
      title: 'Increased Productivity',
      description: 'Focus on core business activities while HR functions are handled professionally.',
      icon: <TrendingUpIcon fontSize="large" color="primary" />
    },
    {
      title: 'Scalability',
      description: 'Easily scale HR resources up or down based on your business needs.',
      icon: <SpeedIcon fontSize="large" color="primary" />
    }
  ];

  // HR services offered
  const services = [
    'Recruitment and onboarding',
    'Employee relations and management',
    'Performance management systems',
    'Compliance management',
    'Payroll and benefits administration',
    'Training and development'
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4, 
            alignItems: 'center'
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                  HR Outsourcing Services
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, fontWeight: 'normal' }}>
                  Professional HR solutions to optimize your workforce management
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="/contact"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: '50px',
                    background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <PeopleIcon sx={{ fontSize: 180, opacity: 0.8 }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6 
        }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom color="primary">
                HR Outsourcing Services
              </Typography>
              <Divider sx={{ mb: 3, width: '70px', borderWidth: 2, borderColor: 'primary.main' }} />
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                Right Track Technologies outsources professional staff to various organizations at competitive rates. Our HR outsourcing services help businesses focus on their core operations while we handle the complexities of human resource management.
              </Typography>

              <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                Our HR Services
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                {services.map((service, index) => (
                  <Box 
                    key={index} 
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <CheckCircleOutlineIcon color="primary" />
                    <Typography variant="body1">{service}</Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1529651737248-dad5e287768e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="HR Outsourcing Services"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ py: 10, background: '#f5f7fa' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h4"
              component="h2"
              textAlign="center"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Benefits of Our HR Outsourcing Services
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
            >
              Optimize your workforce management and focus on your core business
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 4
          }}>
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>{benefit.icon}</Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight="bold"
                    color="primary"
                  >
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2">
                    {benefit.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Flexible Staffing Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: 6
        }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(40% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1573164574472-797cdf4a583a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Flexible Staffing"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(60% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom color="primary">
                Flexible Staffing Solutions
              </Typography>
              <Divider sx={{ mb: 3, width: '70px', borderWidth: 2, borderColor: 'primary.main' }} />
              <Typography variant="body1" paragraph>
                Our HR outsourcing services offer flexible staffing solutions tailored to your specific business needs. Whether you need temporary staff for project-based work or long-term HR support, we provide qualified professionals who can seamlessly integrate with your team.
              </Typography>
              <Typography variant="body1" paragraph>
                Our staffing solutions include:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                {[
                  'Contract staffing for short-term projects',
                  'Temp-to-hire arrangements',
                  'Long-term HR administrative support',
                  'Executive search and placement',
                  'Specialized skill-based recruitment'
                ].map((item, index) => (
                  <Box 
                    key={index} 
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <CheckCircleOutlineIcon color="primary" />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Call to Action Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Ready to optimize your HR functions?
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              Contact us today to discuss how our HR outsourcing services can support your business needs
            </Typography>
            <Button
              variant="contained"
              size="large"
              component="a"
              href="/contact"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                background: 'white',
                color: '#0c2340',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get in Touch
            </Button>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default HrOutsourcing; 